(function (root) {
  'use strict';
  const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
  const photoPattern = new RegExp('^/apps/sac-city-rewards/garage-photos/(' + UUID + ')\\?v=(' + UUID + ')$', 'i');
  const launchStart = Date.parse('2026-10-22T07:00:00.000Z');
  const launchEnd = Date.parse('2026-11-01T07:00:00.000Z');
  function launchActive(enabled, now = Date.now()) { return enabled === true && now >= launchStart && now < launchEnd; }
  function normalizePhotos(payload, vehicleIds) {
    const result = new Map();
    if (payload?.status !== 'READY' || !Array.isArray(payload.photos) || payload.photos.length > 25) return result;
    for (const row of payload.photos) {
      const match = typeof row?.url === 'string' && row.url.match(photoPattern);
      if (!match || row.vehicleId !== match[1] || row.revision !== match[2] || !vehicleIds.includes(row.vehicleId) || result.has(row.vehicleId)) continue;
      result.set(row.vehicleId, {url: row.url, revision: row.revision});
    }
    return result;
  }
  function normalizeWallet(payload, now = Date.now()) {
    if (payload?.status !== 'READY' || !Array.isArray(payload.rewards) || payload.rewards.length > 2) return null;
    const result = {daily: null, monthly: null};
    for (const row of payload.rewards) {
      const expires = Date.parse(row?.expiresAt);
      if (!Number.isFinite(expires) || expires <= now) continue;
      if (row.campaign === 'DAILY' && [5, 10, 15].includes(row.percentOff) && row.amountCents === 0 && !result.daily) result.daily = {value: row.percentOff, expires};
      if (row.campaign === 'MONTHLY' && [500, 1000].includes(row.amountCents) && row.percentOff === 0 && !result.monthly) result.monthly = {value: row.amountCents / 100, expires};
    }
    return result;
  }
  const guestStoreKey = 'sacCity.guestRewards.v1';
  const guestHandoffKey = 'sacCity.guestRewardsHandoff.v1';
  const tokenPattern = /^[0-9a-f]{64}$/;
  const reservationIdPattern = new RegExp('^' + UUID + '$', 'i');
  const guestOutcomes = {NONE: 0, FIVE_PERCENT: 5, TEN_PERCENT: 10, FIFTEEN_PERCENT: 15};
  function normalizeReservation(value, now = Date.now()) {
    if (!value || !reservationIdPattern.test(value.id) || !['DAILY', 'MONTHLY'].includes(value.campaign) || value.currency !== 'USD') return null;
    const created = Date.parse(value.createdAt), expires = Date.parse(value.expiresAt);
    if (!Number.isFinite(created) || !Number.isFinite(expires) || created > now + 60000 || expires <= now || expires <= created || expires - created > 32 * 86400000) return null;
    if (value.campaign === 'DAILY' && (!Object.hasOwn(guestOutcomes, value.outcome) || guestOutcomes[value.outcome] !== value.percentOff || value.amountCents !== 0 || expires - created > 26 * 3600000)) return null;
    if (value.campaign === 'MONTHLY' && (value.outcome !== 'MONTHLY_BONUS' || value.percentOff !== 0 || value.amountCents !== 500)) return null;
    if (typeof value.policyVersion !== 'string' || value.policyVersion.length < 1 || value.policyVersion.length > 100) return null;
    return {id: value.id, campaign: value.campaign, outcome: value.outcome, percentOff: value.percentOff, amountCents: value.amountCents, currency: value.currency, createdAt: value.createdAt, expiresAt: value.expiresAt, policyVersion: value.policyVersion};
  }
  function safeGuestNavigation(value, base, kind, trustedGarageUrl) {
    try {
      const url = new URL(value, base), current = new URL(base);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      if (kind === 'garage') {
        if (typeof trustedGarageUrl !== 'string' || trustedGarageUrl.length > 8192) return null;
        const trusted = new URL(trustedGarageUrl, base);
        const internalGarage = url.origin === current.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/my-garage$/.test(url.pathname) && !url.search;
        const knownRoute = internalGarage || (url.origin === 'https://shopify.com' && url.pathname === '/59377975384/account/pages/01a1611e-155b-746c-b6aa-008618c9fde7') || (url.origin === current.origin && url.pathname === '/account/pages/01a1611e-155b-746c-b6aa-008618c9fde7');
        return knownRoute && !url.hash && url.href === trusted.href ? url.href : null;
      }
      if (kind === 'login') return url.origin === current.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:customer_authentication\/login|account\/login)\/?$/.test(url.pathname) ? url.href : null;
    } catch (_) {}
    return null;
  }
  class GuestRewardFlow {
    constructor({storage, session, crypto, request, now = () => Date.now()}) { this.storage = storage; this.session = session; this.crypto = crypto; this.request = request; this.now = now; this.inflight = new Map(); }
    readStore(create = false, strict = false) {
      try {
        const raw = this.storage.getItem(guestStoreKey);
        let saved = raw && raw.length < 8192 ? JSON.parse(raw) : null;
        if (!saved || !tokenPattern.test(saved.browserToken)) {
          if (!create) { if (strict && raw) throw new Error('STORAGE_UNAVAILABLE'); return null; }
          const bytes = new Uint8Array(32); this.crypto.getRandomValues(bytes);
          saved = {browserToken: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''), reservations: {}};
          this.storage.setItem(guestStoreKey, JSON.stringify(saved));
          if (this.storage.getItem(guestStoreKey) !== JSON.stringify(saved)) throw new Error('STORAGE_UNAVAILABLE');
        }
        const reservations = {};
        for (const kind of ['DAILY', 'MONTHLY']) { const item = normalizeReservation(saved.reservations?.[kind], this.now()); if (item?.campaign === kind) reservations[kind] = item; }
        return {browserToken: saved.browserToken, reservations};
      } catch (_) { if (create || strict) throw new Error('STORAGE_UNAVAILABLE'); return null; }
    }
    writeStore(saved) { try { this.storage.setItem(guestStoreKey, JSON.stringify(saved)); } catch (_) { throw new Error('STORAGE_UNAVAILABLE'); } }
    async post(path, body, signal) {
      const response = await this.request(path, {method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(body), signal});
      let payload; try { payload = await response.json(); } catch (_) { throw new Error('UNAVAILABLE'); }
      if (!payload || typeof payload.status !== 'string' || (!response.ok && ['RESERVED', 'SAVED', 'ALREADY_CLAIMED'].includes(payload.status))) throw new Error('UNAVAILABLE');
      return payload;
    }
    reserve(campaign, signal) {
      if (!['DAILY', 'MONTHLY'].includes(campaign)) return Promise.reject(new Error('UNAVAILABLE'));
      const previous = this.inflight.get(campaign);
      if (previous && !previous.signal?.aborted) return previous.promise;
      const work = (async () => {
        const saved = this.readStore(true);
        const payload = await this.post('/apps/sac-city-rewards/guest-reveal', {browserToken: saved.browserToken, campaign}, signal);
        if (signal?.aborted) throw new Error('CANCELLED');
        const reservation = normalizeReservation(payload.reservation, this.now());
        if (payload.status !== 'RESERVED') throw new Error(['DISABLED', 'RATE_LIMITED', 'SIGN_IN_REQUIRED'].includes(payload.status) ? payload.status : 'UNAVAILABLE');
        if (!reservation || reservation.campaign !== campaign) throw new Error('EXPIRED');
        const fresh = this.readStore(true);
        if (fresh.browserToken !== saved.browserToken) throw new Error('STORAGE_UNAVAILABLE');
        fresh.reservations[campaign] = reservation; this.writeStore(fresh);
        return reservation;
      })().finally(() => { if (this.inflight.get(campaign)?.promise === work) this.inflight.delete(campaign); });
      this.inflight.set(campaign, {promise: work, signal}); return work;
    }
    prepareHandoff() {
      const saved = this.readStore();
      if (!saved) throw new Error('STORAGE_UNAVAILABLE');
      const reservations = Object.values(saved.reservations).map(item => ({id: item.id, expiresAt: item.expiresAt, campaign: item.campaign}));
      if (!reservations.length) throw new Error('EXPIRED');
      const pending = {createdAt: this.now(), reservations};
      try { this.session.setItem(guestHandoffKey, JSON.stringify(pending)); if (this.session.getItem(guestHandoffKey) !== JSON.stringify(pending)) throw new Error(); } catch (_) { throw new Error('STORAGE_UNAVAILABLE'); }
      return pending;
    }
    pending(strict = false) {
      try {
        const raw = this.session.getItem(guestHandoffKey);
        if (!raw) return null;
        if (raw.length > 4096) throw new Error('STORAGE_UNAVAILABLE');
        const pending = JSON.parse(raw);
        if (!Number.isFinite(pending.createdAt) || pending.createdAt > this.now() + 60000 || !Array.isArray(pending.reservations) || !pending.reservations.length || pending.reservations.length > 2) throw new Error('STORAGE_UNAVAILABLE');
        const unique = new Set();
        for (const item of pending.reservations) {
          if (!reservationIdPattern.test(item.id) || !['DAILY', 'MONTHLY'].includes(item.campaign) || unique.has(item.campaign) || !Number.isFinite(Date.parse(item.expiresAt))) throw new Error('STORAGE_UNAVAILABLE');
          unique.add(item.campaign);
        }
        return pending;
      } catch (_) { if (strict) throw new Error('STORAGE_UNAVAILABLE'); return null; }
    }
    clearHandoff() { try { this.session.removeItem(guestHandoffKey); } catch (_) {} }
    completeReservation(id) {
      const saved = this.readStore();
      if (saved) { for (const kind of ['DAILY', 'MONTHLY']) if (saved.reservations[kind]?.id === id) delete saved.reservations[kind]; this.writeStore(saved); }
      const pending = this.pending();
      if (!pending) return;
      pending.reservations = pending.reservations.filter(item => item.id !== id);
      try { if (pending.reservations.length) this.session.setItem(guestHandoffKey, JSON.stringify(pending)); else this.clearHandoff(); } catch (_) { throw new Error('STORAGE_UNAVAILABLE'); }
    }
    async redeemPending({signedIn, campaign = 'MONTHLY', signal, current = () => true}) {
      if (!['DAILY', 'MONTHLY'].includes(campaign)) throw new Error('INVALID_CAMPAIGN');
      if (!signedIn) return [{status: 'SIGN_IN_REQUIRED'}];
      if (signal?.aborted || !current()) throw new Error('CANCELLED');
      let pending = this.pending(true);
      let selected = pending?.reservations.filter(item => item.campaign === campaign) || [];
      let saved;
      // A deliberate Daily click also carries a preview saved before signing in
      // through another route. Automatic Monthly handoff never adopts Daily.
      if (campaign === 'DAILY' && !selected.length) {
        saved = this.readStore(false, true);
        const daily = saved?.reservations.DAILY;
        if (daily) {
          const item = {id: daily.id, expiresAt: daily.expiresAt, campaign: 'DAILY'};
          pending = {createdAt: pending?.createdAt ?? this.now(), reservations: [...(pending?.reservations || []), item]};
          const serialized = JSON.stringify(pending);
          try { this.session.setItem(guestHandoffKey, serialized); if (this.session.getItem(guestHandoffKey) !== serialized) throw new Error(); } catch (_) { throw new Error('STORAGE_UNAVAILABLE'); }
          selected = [item];
        }
      }
      if (!selected.length) return [];
      saved = saved || this.readStore();
      if (!saved) throw new Error('STORAGE_UNAVAILABLE');
      const results = [];
      for (const item of selected) {
        if (signal?.aborted || !current()) throw new Error('CANCELLED');
        if (campaign === 'MONTHLY' && Date.parse(item.expiresAt) <= this.now()) { results.push({status: 'EXPIRED', campaign: item.campaign}); this.completeReservation(item.id); continue; }
        const payload = await this.post('/apps/sac-city-rewards/guest-redeem', {browserToken: saved.browserToken, reservationId: item.id}, signal);
        if (signal?.aborted || !current()) throw new Error('CANCELLED');
        const terminal = ['SAVED', 'ALREADY_CLAIMED', 'COOLDOWN', 'EXPIRED', 'NOT_FOUND', 'CLAIMED_ELSEWHERE'];
        if (!terminal.includes(payload.status)) throw new Error(payload.status === 'SIGN_IN_REQUIRED' ? 'SIGN_IN_REQUIRED' : payload.status === 'RATE_LIMITED' ? 'RATE_LIMITED' : 'UNAVAILABLE');
        let result = {status: payload.status, campaign: item.campaign};
        if (payload.status === 'SAVED') {
          const reservation = normalizeReservation(payload.reservation, this.now());
          if (!reservation || reservation.id !== item.id || reservation.campaign !== item.campaign) throw new Error('UNAVAILABLE');
          if (reservation.outcome === 'NONE' && payload.reward == null) result = {...result, outcome: 'NONE'};
          else {
            const reward = payload.reward;
            if (!reward || reward.campaign !== reservation.campaign || reward.percentOff !== reservation.percentOff || reward.amountCents !== reservation.amountCents || reward.currency !== 'USD' || !['PENDING_ISSUANCE', 'ISSUED'].includes(reward.status) || Date.parse(reward.expiresAt) <= this.now() || !Number.isFinite(Date.parse(reward.expiresAt))) throw new Error('UNAVAILABLE');
          }
        }
        results.push(result); this.completeReservation(item.id);
      }
      return results;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {launchActive, normalizePhotos, normalizeWallet, normalizeReservation, safeGuestNavigation, GuestRewardFlow, guestStoreKey, guestHandoffKey};
  if (root?.document && !root.SacGarageGuestRewards) {
    let guestBridge;
    root.SacGarageGuestRewards = Object.freeze({
      async redeemPending({campaign = 'MONTHLY', signal, current = () => true} = {}) {
        if (root.document.hidden || signal?.aborted || !current()) throw new Error('CANCELLED');
        if (!guestBridge) guestBridge = new GuestRewardFlow({storage: root.localStorage, session: root.sessionStorage, crypto: root.crypto, request: (...args) => root.fetch(...args)});
        return guestBridge.redeemPending({signedIn: true, campaign, signal, current: () => !root.document.hidden && !signal?.aborted && current()});
      }
    });
  }
  if (!root?.document || !root.customElements || root.customElements.get('sac-member-garage')) return;
  class SacMemberGarage extends root.HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      try { this.config = JSON.parse(this.querySelector('[data-member-config]').textContent); } catch (_) { return; }
      this.strings = this.config.strings || {};
      this.photos = new Map(); this.wallet = null; this.requestKey = ''; this.generation = 0; this.step = 'car';
      this.store = root.SacGarageSelection;
      try { this.guest = new GuestRewardFlow({storage: root.localStorage, session: root.sessionStorage, crypto: root.crypto, request: (...args) => fetch(...args)}); } catch (_) { this.guest = null; }
      this.guestEpoch = 0; this.guestBusy = false; this.handoffBusy = false; this.handoffAttempted = false;
      this.cleanups = [];
      const listen = (target, name, fn) => { target.addEventListener(name, fn); this.cleanups.push(() => target.removeEventListener(name, fn)); };
      listen(this.querySelector('[data-member-select]'), 'change', event => this.store?.select(event.target.value || null));
      this.querySelectorAll('[data-member-step]').forEach(button => listen(button, 'click', () => {
        this.step = button.dataset.memberStep;
        this.querySelectorAll('[data-member-step]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        this.text('hint', this.t('step_' + this.step + '_copy'));
        this.querySelector('[data-member-garage-link]').href = this.config.garageUrl;
        this.text('garage-label', this.t('add_car')); 
      }));
      const dialog = this.querySelector('[data-member-preview]');
      ['daily', 'monthly'].forEach(kind => {
        const trigger = this.querySelector('[data-member-' + kind + '-link]');
        listen(trigger, 'click', event => {
          if (!this.isSignedIn() && this.openPreview(kind, trigger)) event.preventDefault();
        });
        listen(trigger, 'keydown', event => {
          if (event.key === ' ' && !this.isSignedIn() && this.openPreview(kind, trigger)) event.preventDefault();
        });
      });
      listen(this.querySelector('[data-member-preview-open]'), 'click', () => void this.unwrapPreview());
      listen(this.querySelector('[data-member-preview-join]'), 'click', event => { if (!this.beginSignup()) event.preventDefault(); });
      // Any normal same-window Garage entry on this page must carry an already revealed offer.
      // Keep account/order destinations and modified/new-tab clicks under normal browser control.
      const carryGarageOffer = (event, link) => {
        if (event.defaultPrevented || this.isSignedIn() || (event.button != null && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !link) return;
        if ((link.getAttribute('target') && link.getAttribute('target') !== '_self') || link.getAttribute('download') !== null) return;
        if (!safeGuestNavigation(link.href, root.location.href, 'garage', this.config.garageUrl)) return;
        const reservation = Object.values(this.guest?.readStore()?.reservations || {})[0];
        if (!reservation) return;
        event.preventDefault(); this.previewReservation = reservation; this.previewJoinMode = 'signup'; this.beginSignup();
      };
      ['garage-link', 'account'].forEach(name => {
        const link = this.querySelector('[data-member-' + name + ']');
        listen(link, 'click', event => carryGarageOffer(event, link));
      });
      listen(root.document, 'click', event => carryGarageOffer(event, event.target?.closest?.('a[href]')));
      listen(this.querySelector('[data-member-preview-close]'), 'click', () => this.closePreview());
      listen(dialog, 'cancel', event => { event.preventDefault(); this.closePreview(); });
      listen(dialog, 'close', () => this.finishPreviewClose());
      listen(dialog, 'click', event => { if (event.target === dialog) this.closePreview(); });
      listen(this.querySelector('[data-member-photo]'), 'error', () => {
        const image = this.querySelector('[data-member-photo]');
        if (image.getAttribute('src')) { this.photos.delete(this.state?.selectedId); this.clearPhoto(); this.text('placeholder-label', this.t('photo_unavailable')); }
      });
      listen(root, 'pagehide', () => this.suspend());
      listen(root.document, 'visibilitychange', () => {
        if (root.document.hidden) this.suspend();
        else { this.updateRewards(); this.render(); }
      });
      listen(root, 'pageshow', event => { if (event.persisted) this.render(); });
      if (this.store) this.unsubscribe = this.store.subscribe(() => this.render());
      this.render();
    }
    disconnectedCallback() {
      this.closePreview(false);
      this.guestAbort?.abort(); this.handoffAbort?.abort(); this.abort?.abort(); this.unsubscribe?.(); this.cleanups?.forEach(fn => fn());
      root.clearTimeout(this.tick); this.cleanups = []; this.generation = (this.generation || 0) + 1;
    }
    t(key) { return this.strings[key] || ''; }
    text(name, value) { const node = this.querySelector('[data-member-' + name + ']'); if (node) node.textContent = value; }
    show(name, visible) { const node = this.querySelector('[data-member-' + name + ']'); if (node) node.hidden = !visible; }
    isSignedIn() {
      return !!(this.state?.signedIn && !this.state.pending && !this.state.failed && this.state.garageAvailable !== false && !root.document.hidden);
    }
    openPreview(kind, trigger) {
      const dialog = this.querySelector('[data-member-preview]');
      if (this.isSignedIn() || !['daily', 'monthly'].includes(kind) || typeof dialog?.showModal !== 'function') return false;
      this.guestAbort?.abort(); this.guestEpoch = (this.guestEpoch || 0) + 1;
      this.previewKind = kind; this.previewTrigger = trigger; this.previewReturnFocus = true; this.previewReservation = null;
      dialog.dataset.unwrapped = 'false';
      this.text('preview-badge', this.t('reservation_badge'));
      this.text('preview-title', this.t('reservation_loading_title'));
      this.text('preview-copy', this.t('reservation_loading_copy'));
      this.text('preview-value', '?'); this.text('preview-note', '');
      this.show('preview-note', false); this.show('preview-next', false); this.show('preview-open', false);
      this.setPreviewJoin('continue');
      try { if (!dialog.open) dialog.showModal(); } catch (_) { this.previewTrigger = null; return false; }
      trigger.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-member-preview-close]').focus();
      void this.unwrapPreview();
      return true;
    }
    setPreviewJoin(mode) {
      this.previewJoinMode = mode;
      const link = this.querySelector('[data-member-preview-join]');
      const base = root.location?.href || 'https://saccityautoparts.com/';
      link.href = safeGuestNavigation(mode === 'signup' ? this.config.loginUrl : this.config.garageUrl, base, mode === 'signup' ? 'login' : 'garage', this.config.garageUrl) || this.config.garageUrl;
      link.removeAttribute('target');
      this.text('preview-join-label', this.t(mode === 'signup' ? 'reservation_signup' : 'handoff_continue'));
    }
    async unwrapPreview() {
      if (this.previewKind === 'handoff') { this.handoffAttempted = false; return this.resumeHandoff(true); }
      const dialog = this.querySelector('[data-member-preview]');
      if (!dialog?.open || this.isSignedIn() || !['daily', 'monthly'].includes(this.previewKind) || this.guestBusy) return;
      const kind = this.previewKind, epoch = ++this.guestEpoch, controller = new AbortController();
      this.guestAbort?.abort(); this.guestAbort = controller; this.guestBusy = true;
      this.text('preview-title', this.t('reservation_loading_title')); this.text('preview-copy', this.t('reservation_loading_copy'));
      this.show('preview-open', false); this.show('preview-next', false); this.show('preview-note', false);
      const timeout = root.setTimeout(() => controller.abort(), 12000);
      const current = () => epoch === this.guestEpoch && this.isConnected && dialog.open && !root.document.hidden && !this.isSignedIn();
      try {
        if (!this.guest) throw new Error('STORAGE_UNAVAILABLE');
        const reservation = await this.guest.reserve(kind.toUpperCase(), controller.signal);
        if (!current()) return;
        this.previewReservation = reservation;
        if (reservation.outcome === 'NONE') {
          this.text('preview-value', '—'); this.text('preview-title', this.t('reservation_none_title')); this.text('preview-copy', this.t('reservation_none_copy'));
        } else {
          this.amount('preview-value', reservation.campaign === 'MONTHLY' ? reservation.amountCents / 100 : reservation.percentOff, reservation.campaign === 'MONTHLY' ? '$' : '%');
          this.text('preview-title', this.t(reservation.campaign === 'MONTHLY' ? 'reservation_monthly_title' : 'reservation_daily_title'));
          this.text('preview-copy', this.t('reservation_copy'));
        }
        this.text('preview-note', this.t('reservation_note') + ' ' + this.expiry(reservation.expiresAt));
        dialog.dataset.unwrapped = 'true'; this.show('preview-note', true); this.show('preview-next', true);
        this.setPreviewJoin('signup'); this.querySelector('[data-member-preview-join]').focus();
      } catch (error) {
        if (current()) this.previewFailure(error?.message);
      } finally { root.clearTimeout(timeout); if (epoch === this.guestEpoch) this.guestBusy = false; }
    }
    expiry(value) {
      return this.t('expires') + ' ' + new Intl.DateTimeFormat('en-US', {timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'}).format(new Date(Date.parse(value) - 1)) + ' ' + this.t('pacific');
    }
    previewFailure(code) {
      this.text('preview-value', '?'); this.querySelector('[data-member-preview]').dataset.unwrapped = 'false';
      this.text('preview-title', this.t(code === 'EXPIRED' ? 'reservation_expired_title' : 'reservation_error_title'));
      this.text('preview-copy', this.t(code === 'EXPIRED' ? 'reservation_expired_copy' : code === 'STORAGE_UNAVAILABLE' ? 'reservation_storage_copy' : code === 'RATE_LIMITED' ? 'reservation_rate_copy' : 'reservation_error_copy'));
      this.show('preview-note', false); this.show('preview-next', true); this.show('preview-open', code !== 'STORAGE_UNAVAILABLE');
      this.text('preview-open-label', this.t('reservation_retry')); this.setPreviewJoin('continue');
    }
    beginSignup() {
      if (this.previewJoinMode !== 'signup') {
        return true;
      }
      try {
        if (!this.previewReservation || !normalizeReservation(this.previewReservation)) throw new Error('EXPIRED');
        if (!this.guest) throw new Error('STORAGE_UNAVAILABLE');
        const url = safeGuestNavigation(this.config.loginUrl, root.location.href, 'login');
        if (!url) throw new Error('UNAVAILABLE');
        this.guest.prepareHandoff();
        root.location.assign(url);
      } catch (error) {
        const dialog = this.querySelector('[data-member-preview]');
        try { if (!dialog.open) dialog.showModal(); } catch (_) {}
        this.previewFailure(error?.message);
      }
      return false;
    }
    async resumeHandoff(force = false) {
      if (!this.guest || !this.isSignedIn() || this.handoffBusy || (this.handoffAttempted && !force) || !this.guest.pending()?.reservations.some(item => item.campaign === 'MONTHLY')) return;
      const dialog = this.querySelector('[data-member-preview]');
      if (typeof dialog?.showModal !== 'function') return;
      this.handoffAttempted = true; this.handoffBusy = true; this.previewKind = 'handoff'; this.previewReturnFocus = false;
      this.guestAbort?.abort(); this.guestEpoch = (this.guestEpoch || 0) + 1; this.guestBusy = false;
      const controller = new AbortController(); this.handoffAbort = controller;
      dialog.dataset.unwrapped = 'false'; this.text('preview-value', '?'); this.text('preview-badge', this.t('reservation_badge'));
      this.text('preview-title', this.t('handoff_title')); this.text('preview-copy', this.t('handoff_copy'));
      this.show('preview-open', false); this.show('preview-next', false); this.show('preview-note', false);
      try { if (!dialog.open) dialog.showModal(); } catch (_) { this.handoffBusy = false; return; }
      const timeout = root.setTimeout(() => controller.abort(), 20000);
      const current = () => this.isConnected && !controller.signal.aborted && this.isSignedIn() && !root.document.hidden;
      try {
        const results = await this.guest.redeemPending({signedIn: true, campaign: 'MONTHLY', signal: controller.signal, current});
        if (!current()) return;
        this.show('preview-next', true); this.setPreviewJoin('continue');
        const conflict = results.find(item => item.status !== 'SAVED'), noReward = results.find(item => item.outcome === 'NONE');
        if (!conflict && !noReward && results.length) {
          this.text('preview-title', this.t('handoff_saved_title')); this.text('preview-copy', this.t('handoff_saved_copy'));
          const url = safeGuestNavigation(this.config.garageUrl, root.location.href, 'garage', this.config.garageUrl);
          if (url) root.location.assign(url);
        } else {
          const prefix = noReward && !conflict ? 'handoff_none' : conflict?.status === 'ALREADY_CLAIMED' ? 'handoff_existing' : conflict?.status === 'COOLDOWN' ? 'handoff_cooldown' : 'reservation_expired';
          this.text('preview-title', this.t(prefix + '_title')); this.text('preview-copy', this.t(prefix + '_copy'));
          this.querySelector('[data-member-preview-join]').focus();
        }
        this.requestKey = ''; this.loadPrivate((this.state?.vehicles || []).map(vehicle => vehicle.id));
      } catch (error) {
        if (this.isConnected && this.isSignedIn() && !root.document.hidden) {
          this.text('preview-title', this.t('handoff_error_title')); this.text('preview-copy', this.t('handoff_error_copy'));
          this.show('preview-next', true); this.setPreviewJoin('continue'); this.show('preview-open', true); this.text('preview-open-label', this.t('handoff_retry'));
        }
      } finally { root.clearTimeout(timeout); this.handoffBusy = false; }
    }
    closePreview(restoreFocus = true) {
      this.handoffAbort?.abort();
      this.guestAbort?.abort(); this.guestEpoch = (this.guestEpoch || 0) + 1; this.guestBusy = false;
      this.previewReturnFocus = restoreFocus;
      const dialog = this.querySelector('[data-member-preview]');
      if (dialog?.open && typeof dialog.close === 'function') dialog.close();
      else this.finishPreviewClose();
    }
    finishPreviewClose() {
      const trigger = this.previewTrigger; this.previewTrigger = null; this.previewKind = null;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (this.previewReturnFocus && trigger.isConnected && !root.document.hidden) trigger.focus();
      }
    }
    clearPhoto() {
      const image = this.querySelector('[data-member-photo]');
      image.hidden = true; image.removeAttribute('src'); image.alt = '';
      this.show('placeholder', true); this.querySelector('[data-member-stage]').dataset.hasPhoto = 'false';
    }
    suspend() {
      this.handoffAbort?.abort(); this.handoffAttempted = false; this.closePreview(false);
      this.abort?.abort(); this.generation++; this.requestKey = ''; this.photos.clear(); this.wallet = null;
      this.clearPhoto(); this.text('heading', this.t('heading')); this.show('vehicle', false);
      this.show('intro', true); this.text('intro-title', this.t('intro_title')); this.text('hint', this.t('refreshing'));
      this.updateRewards();
    }
    render() {
      this.state = this.store?.getState() || {signedIn: false, vehicles: []};
      const state = this.state;
      if (state.pending || root.document.hidden) { this.suspend(); return; }
      const signedIn = state.signedIn && !state.failed && state.garageAvailable !== false;
      const vehicles = signedIn ? state.vehicles : [];
      const selected = signedIn ? state.vehicle : null;
      if (signedIn && this.previewKind !== 'handoff') this.closePreview(false);
      if (!signedIn) { this.handoffAbort?.abort(); this.handoffAttempted = false; }
      this.text('heading', this.t(signedIn ? 'heading_member' : 'heading'));
      this.text('garage-note', this.t(vehicles.length ? 'saved_for_you' : 'yours'));
      const account = this.querySelector('[data-member-account]');
      account.href = signedIn ? this.config.accountUrl : this.config.garageUrl;
      account.replaceChildren(root.document.createTextNode(this.t(signedIn ? 'my_account' : 'sign_in') + ' ↗'));
      this.show('vehicle', vehicles.length > 0); this.show('intro', !vehicles.length); this.show('steps', !signedIn);
      this.show('join-note', !signedIn); this.show('shop-link', vehicles.length > 0); this.show('photo-link', !!selected);
      this.text('intro-title', this.t(signedIn ? 'empty_title' : 'intro_title'));
      this.text('hint', this.t(state.failed ? 'garage_unavailable' : signedIn ? 'empty_copy' : 'step_' + this.step + '_copy'));
      this.querySelector('[data-member-garage-link]').href = this.config.garageUrl;
      this.text('garage-label', this.t(vehicles.length ? 'manage_garage' : 'add_car')); 
      const select = this.querySelector('[data-member-select]');
      if (this.optionsKey !== JSON.stringify(vehicles)) {
        this.optionsKey = JSON.stringify(vehicles); select.replaceChildren();
        const empty = root.document.createElement('option'); empty.value = ''; empty.textContent = this.t('choose_vehicle'); select.append(empty);
        vehicles.forEach(vehicle => { const option = root.document.createElement('option'); option.value = vehicle.id; option.textContent = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ') + (vehicle.nickname ? ' · ' + vehicle.nickname : ''); select.append(option); });
      }
      select.value = state.selectedId || '';
      this.renderPhoto(); this.updateRewards();
      if (!signedIn) { this.abort?.abort(); this.generation++; this.requestKey = ''; this.photos.clear(); this.wallet = null; this.clearPhoto(); this.updateRewards(); return; }
      const key = JSON.stringify(vehicles.map(vehicle => [vehicle.id, vehicle.updatedAt]));
      if (this.requestKey !== key) { this.requestKey = key; this.loadPrivate(vehicles.map(vehicle => vehicle.id)); }
      void this.resumeHandoff();
    }
    renderPhoto() {
      const selected = this.state?.vehicle;
      const photo = selected && this.photos.get(selected.id);
      this.text('placeholder-label', this.t(selected ? 'your_photo_here' : this.state?.vehicles?.length ? 'choose_vehicle' : 'park_here'));
      this.text('photo-label', this.t(photo ? 'edit_photo' : 'add_photo'));
      if (!photo) { this.clearPhoto(); return; }
      const image = this.querySelector('[data-member-photo]');
      image.alt = [selected.year, selected.make, selected.model].join(' ');
      if (image.getAttribute('src') !== photo.url) image.src = photo.url;
      image.hidden = false; this.show('placeholder', false); this.querySelector('[data-member-stage]').dataset.hasPhoto = 'true';
    }
    async loadPrivate(vehicleIds) {
      this.abort?.abort(); const controller = new AbortController(); this.abort = controller;
      const generation = ++this.generation;
      const current = () => this.isConnected && generation === this.generation && !controller.signal.aborted && !root.document.hidden && this.state?.signedIn && !this.state?.pending;
      const timeout = root.setTimeout(() => controller.abort(), 7000);
      const read = async path => {
        const response = await fetch(path + '?_nonce=' + Date.now(), {credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Unavailable');
        return response.json();
      };
      // This refresh only reads private photos and the existing wallet.
      await Promise.allSettled([
        read('/apps/sac-city-rewards/garage-photos').then(payload => { if (current()) { this.photos = normalizePhotos(payload, vehicleIds); this.renderPhoto(); } }).catch(() => { if (current()) { this.photos.clear(); this.renderPhoto(); } }),
        read('/apps/sac-city-rewards/auto-apply').then(payload => { if (current()) { this.wallet = normalizeWallet(payload); this.updateRewards(); } }).catch(() => { if (current()) { this.wallet = null; this.updateRewards(); } })
      ]);
      root.clearTimeout(timeout);
    }
    updateRewards() {
      if (!this.config) return;
      const now = Date.now(), launch = launchActive(this.dataset.launchEnabled === 'true', now);
      const signedIn = this.isSignedIn();
      const daily = signedIn && this.wallet?.daily?.expires > now ? this.wallet.daily : null;
      const monthly = signedIn && this.wallet?.monthly?.expires > now ? this.wallet.monthly : null;
      const hasRewards = !!(daily || monthly);
      this.text('perks-eyebrow', this.t(hasRewards ? 'saved_eyebrow' : 'perks_eyebrow'));
      this.text('perks-title', this.t(hasRewards ? 'saved_heading' : 'perks_heading'));
      this.text('perks-copy', this.t(hasRewards ? 'saved_copy' : 'perks_copy'));
      this.text('daily-title', this.t(launch ? 'daily_title' : 'check_in'));
      this.text('daily-status', this.t(daily ? 'ready_to_use' : signedIn ? 'member_perk' : 'ready_for_you'));
      this.text('monthly-status', this.t(monthly ? 'ready_to_use' : signedIn ? 'member_perk' : 'ready_for_you'));
      this.text('daily-prefix', this.t(!signedIn ? 'mystery_prefix' : daily ? 'saved' : 'up_to'));
      this.text('monthly-prefix', this.t(!signedIn ? 'mystery_prefix' : monthly ? 'saved' : 'every_month'));
      this.text('daily-suffix', this.t(signedIn ? 'off' : 'mystery_daily_suffix'));
      this.text('monthly-suffix', this.t(signedIn ? 'to_shop' : 'mystery_monthly_suffix'));
      if (signedIn) {
        this.amount('daily-value', daily?.value || (launch ? 15 : 10), '%');
        this.amount('monthly-value', monthly?.value || 5, '$');
      } else { this.text('daily-value', '?'); this.text('monthly-value', '?'); }
      ['daily', 'monthly'].forEach(kind => {
        const link = this.querySelector('[data-member-' + kind + '-link]');
        link.href = signedIn && kind !== 'daily' ? this.config.rewardsUrl : this.config.garageUrl;
        if (signedIn && kind === 'daily') link.setAttribute('data-sac-garage-entry', '');
        else link.removeAttribute('data-sac-garage-entry');
        this.querySelector('[data-member-' + kind + '-card]').dataset.mystery = String(!signedIn);
        if (!signedIn && typeof this.querySelector('[data-member-preview]')?.showModal === 'function') {
          link.setAttribute('role', 'button'); link.setAttribute('aria-haspopup', 'dialog');
          link.setAttribute('aria-expanded', String(this.previewKind === kind));
        } else { link.removeAttribute('role'); link.removeAttribute('aria-haspopup'); link.removeAttribute('aria-expanded'); }
      });
      const expiry = reward => this.t('expires') + ' ' + new Intl.DateTimeFormat('en-US', {timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'}).format(new Date(reward.expires - 1)) + ' ' + this.t('pacific');
      this.text('daily-note', !signedIn ? this.t('mystery_daily_note') : daily ? expiry(daily) : this.t(launch ? 'daily_launch_note' : 'daily_standard_note'));
      this.text('monthly-note', !signedIn ? this.t('mystery_monthly_note') : monthly ? expiry(monthly) : this.t('monthly_note'));
      this.text('daily-footer', this.t(signedIn ? 'member_daily_footer' : 'daily_footer'));
      this.text('monthly-footer', this.t(signedIn ? 'member_monthly_footer' : 'monthly_footer'));
      this.text('daily-action', this.t(!signedIn ? 'mystery_daily_action' : daily ? 'use_reward' : 'check_reward'));
      this.text('monthly-action', this.t(!signedIn ? 'mystery_monthly_action' : monthly ? 'use_bonus' : 'view_bonus'));
      this.querySelector('[data-member-daily-card]').dataset.saved = String(!!daily);
      this.querySelector('[data-member-monthly-card]').dataset.saved = String(!!monthly);
      this.text('wallet-note', this.t(hasRewards ? 'saved_wallet_note' : signedIn ? 'member_wallet_note' : 'mystery_wallet_note'));
      this.text('policy', this.t(launch ? 'launch_terms' : 'standard_terms'));
      root.clearTimeout(this.tick);
      // Expire displayed coupons at their exact server-issued timestamp. Re-check the
      // clock periodically so device clock changes and long-lived tabs stay accurate.
      const boundaries = [daily?.expires, monthly?.expires, launch ? launchEnd : null].filter(value => Number.isFinite(value) && value > now);
      const delay = Math.min(30000, ...boundaries.map(value => value - now + 1));
      if (this.isConnected && !root.document.hidden) this.tick = root.setTimeout(() => this.updateRewards(), delay);
    }
    amount(name, value, unit) {
      const node = this.querySelector('[data-member-' + name + ']');
      const span = root.document.createElement('span'); span.textContent = unit;
      const number = root.document.createTextNode(String(value));
      node.replaceChildren(...(unit === '$' ? [span, number] : [number, span]));
    }
  }
  root.customElements.define('sac-member-garage', SacMemberGarage);
})(typeof window === 'undefined' ? null : window);
